import type { ComponentType, SVGProps } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils/cn';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useSurahList } from '@/hooks/useSurah';
import {
  useAyahReadDetails,
  useBookmarksCount,
  useQuranJuzIndex,
  useReadingStreak,
  useReadsByAllSurahs,
} from '@/hooks/useStudentStats';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  IconAward,
  IconBookmark,
  IconCheckCircle,
  IconClock,
  IconFlame,
  IconShare,
  IconStar,
  IconTrendUp,
} from '@/components/home/icons';

type IconCmp = ComponentType<SVGProps<SVGSVGElement>>;

const TOTAL_AYAHS = 6236;
const TOTAL_SURAHS = 114;
const TOTAL_JUZ = 30;
const MINUTES_PER_AYAH_ESTIMATE = 1.5;

const fmtDate = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const localDayFmt = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function StudentPortalDashboard() {
  useDocumentTitle('Student Portal');

  const surahs = useSurahList();
  const bySurah = useReadsByAllSurahs();
  const reads = useAyahReadDetails();
  const juzIndex = useQuranJuzIndex();
  const streak = useReadingStreak();
  const bookmarks = useBookmarksCount();

  const loading =
    surahs.isLoading ||
    bySurah.isLoading ||
    reads.isLoading ||
    juzIndex.isLoading ||
    streak.isLoading ||
    bookmarks.isLoading;

  const totalRead = reads.data?.length ?? 0;
  const completionPct = Math.min(100, Math.round((totalRead / TOTAL_AYAHS) * 100));
  const completedSurahs = (surahs.data ?? []).filter((surah) => {
    const read = bySurah.data?.get(surah.id) ?? 0;
    return read >= surah.ayah_count;
  }).length;

  const readKeys = new Set(
    (reads.data ?? []).map((read) => ayahKey(read.surah_id, read.ayah_number)),
  );
  const juzTotals = new Map<number, number>();
  const juzReads = new Map<number, number>();
  for (const row of juzIndex.data ?? []) {
    if (!row.juz) continue;
    const key = ayahKey(row.surah_id, row.ayah_number);
    juzTotals.set(row.juz, (juzTotals.get(row.juz) ?? 0) + 1);
    if (readKeys.has(key)) {
      juzReads.set(row.juz, (juzReads.get(row.juz) ?? 0) + 1);
    }
  }
  const completedJuz = Array.from(juzTotals.entries()).filter(
    ([juz, total]) => (juzReads.get(juz) ?? 0) >= total,
  ).length;

  const uniqueDays = new Set(
    (reads.data ?? []).map((read) => localDayFmt.format(new Date(read.read_at))),
  );
  const todayKey = localDayFmt.format(new Date());
  const todayReads = (reads.data ?? []).filter(
    (read) => localDayFmt.format(new Date(read.read_at)) === todayKey,
  ).length;
  const lastReadDate = reads.data?.[0]?.read_at
    ? fmtDate.format(new Date(reads.data[0].read_at))
    : 'No reads yet';
  const estimatedMinutes = Math.round(totalRead * MINUTES_PER_AYAH_ESTIMATE);
  const currentStreak = streak.data?.current ?? 0;
  const longestStreak = streak.data?.longest ?? 0;
  const activityItems = [
    { label: 'Surahs', value: completedSurahs, max: TOTAL_SURAHS, display: String(completedSurahs) },
    { label: 'Ayahs', value: totalRead, max: TOTAL_AYAHS, display: completionPct + '%' },
    { label: 'Juz', value: completedJuz, max: TOTAL_JUZ, display: String(completedJuz) },
    { label: 'Today', value: todayReads, max: Math.max(10, todayReads), display: String(todayReads) },
    { label: 'Streak', value: currentStreak, max: Math.max(7, longestStreak, currentStreak), display: String(currentStreak) },
    { label: 'Days', value: uniqueDays.size, max: Math.max(30, uniqueDays.size), display: String(uniqueDays.size) },
  ];

  return (
    <main className="min-h-screen bg-ink-50 text-ink-900 dark:bg-night-900 dark:text-night-50">
      <section className="container-wide space-y-4 py-5 sm:py-8">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-ink-500 dark:text-night-200">
              Home / Student Portal
            </p>
            <h1 className="mt-2 text-3xl font-bold text-ink-950 sm:text-4xl dark:text-night-50">
              Student Portal
            </h1>
          </div>
          <button
            type="button"
            aria-label="Share Student Portal"
            title="Share"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-ink-200 bg-white text-ink-700 transition-colors hover:border-accent/40 hover:text-accent dark:border-night-500/30 dark:bg-night-800 dark:text-night-200 dark:hover:text-beam"
          >
            <IconShare className="h-5 w-5" />
          </button>
        </header>

        <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            Icon={IconCheckCircle}
            label="Surahs Completed"
            value={completedSurahs}
            suffix={'/ ' + TOTAL_SURAHS}
            loading={loading}
            featured
            delay={0}
          />
          <MetricCard
            Icon={IconTrendUp}
            label="Ayahs Completed"
            value={totalRead}
            suffix={'/ ' + numberText(TOTAL_AYAHS)}
            helper={completionPct + '% overall'}
            loading={loading}
            delay={60}
          />
          <MetricCard
            Icon={IconAward}
            label="Juz Completed"
            value={completedJuz}
            suffix={'/ ' + TOTAL_JUZ}
            loading={loading}
            delay={120}
          />
          <MetricCard
            Icon={IconClock}
            label="Time Spent"
            value={formatMinutes(estimatedMinutes)}
            helper="Estimated from ayahs"
            loading={loading}
            delay={180}
          />
        </section>

        <section className="grid gap-3 lg:grid-cols-[1.6fr_0.8fr]">
          <DashboardCard className="motion-safe:animate-[studentPortalRise_520ms_ease-out_120ms_both]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-ink-950 dark:text-night-50">
                  Reading Activity
                </h2>
                <p className="mt-1 text-sm text-ink-500 dark:text-night-200">
                  {fmtDate.format(new Date())}
                </p>
              </div>
              <RadialMeter value={completionPct} />
            </div>
            <ActivityChart items={activityItems} loading={loading} />
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <TileMetric
                Icon={IconFlame}
                label="Current Streak"
                value={currentStreak + ' Days'}
                loading={loading}
              />
              <TileMetric
                Icon={IconStar}
                label="Longest Read"
                value={longestStreak + ' Days'}
                loading={loading}
              />
              <TileMetric
                Icon={IconBookmark}
                label="Bookmarks"
                value={String(bookmarks.data ?? 0)}
                loading={loading}
              />
            </div>
          </DashboardCard>

          <DashboardCard className="motion-safe:animate-[studentPortalRise_520ms_ease-out_180ms_both] space-y-3">
            <h2 className="text-lg font-bold text-ink-950 dark:text-night-50">
              Details
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
              <MiniStat label="Last read" value={loading ? null : lastReadDate} />
              <MiniStat label="Today" value={loading ? null : todayReads + ' ayahs'} />
              <MiniStat label="Active days" value={loading ? null : String(uniqueDays.size)} />
              <MiniStat label="Completion" value={loading ? null : completionPct + '%'} />
            </div>
            <DotMatrix count={24} active={Math.min(24, uniqueDays.size)} small />
            {loading ? (
              <Skeleton className="h-10 w-full" />
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Link
                  to="/reader"
                  className="inline-flex items-center justify-center rounded-lg bg-accent px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-accent-600"
                >
                  Read
                </Link>
                <Link
                  to="/surah"
                  className="inline-flex items-center justify-center rounded-lg border border-ink-200 px-3 py-2 text-sm font-semibold text-ink-700 transition-colors hover:border-accent/40 hover:text-accent dark:border-night-500/30 dark:text-night-100 dark:hover:text-beam"
                >
                  Surahs
                </Link>
              </div>
            )}
          </DashboardCard>
        </section>
      </section>
      <style>
        {`
          @keyframes studentPortalRise {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          @keyframes studentPortalBar {
            from { transform: scaleY(0); }
            to { transform: scaleY(1); }
          }
          @media (prefers-reduced-motion: reduce) {
            .student-portal-bar {
              animation: none !important;
              transform: none !important;
            }
          }
        `}
      </style>
    </main>
  );
}

function DashboardCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <section className={cn('surface-card p-4 shadow-sm sm:p-5', className)}>{children}</section>;
}

function MetricCard({
  Icon,
  label,
  value,
  suffix = '',
  helper,
  loading,
  featured = false,
  delay = 0,
}: {
  Icon: IconCmp;
  label: string;
  value: number | string;
  suffix?: string;
  helper?: string;
  loading: boolean;
  featured?: boolean;
  delay?: number;
}) {
  return (
    <section
      className={cn(
        'surface-card p-4 shadow-sm transition-transform duration-200 hover:-translate-y-0.5',
        'motion-safe:animate-[studentPortalRise_520ms_ease-out_both]',
        featured && 'bg-accent-50/80 dark:bg-beam/10',
      )}
      style={{ animationDelay: delay + 'ms' }}
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-ink-500 dark:text-night-200">{label}</p>
          {loading ? (
            <Skeleton className="mt-2 h-9 w-28" />
          ) : (
            <p className="mt-1 text-2xl font-bold text-ink-950 num-tight dark:text-night-50">
              {typeof value === 'number' ? numberText(value) : value}
              {suffix && <span className="ml-1 text-base font-semibold text-ink-500 dark:text-night-200">{suffix}</span>}
            </p>
          )}
        </div>
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white text-accent shadow-sm ring-1 ring-ink-200/70 dark:bg-night-800 dark:text-beam dark:ring-night-500/30">
          <Icon className="h-5 w-5" />
        </span>
      </div>
      {helper && (
        <p className="mt-4 inline-flex rounded-full bg-white/80 px-2.5 py-1 text-xs font-semibold text-ink-600 ring-1 ring-ink-200/70 dark:bg-night-800/80 dark:text-night-200 dark:ring-night-500/30">
          {helper}
        </p>
      )}
    </section>
  );
}

function TileMetric({
  Icon,
  label,
  value,
  loading,
}: {
  Icon: IconCmp;
  label: string;
  value: string;
  loading: boolean;
}) {
  return (
    <div className="rounded-xl border border-ink-200/70 bg-white/50 p-3 dark:border-night-500/30 dark:bg-night-800/60">
      <Icon className="h-5 w-5 text-accent dark:text-beam" />
      <p className="mt-4 text-sm text-ink-500 dark:text-night-200">{label}</p>
      {loading ? (
        <Skeleton className="mt-2 h-7 w-20" />
      ) : (
        <p className="mt-1 text-xl font-bold text-ink-950 num-tight dark:text-night-50">
          {value}
        </p>
      )}
    </div>
  );
}

function MiniStat({
  label,
  value,
}: {
  label: string;
  value: string | null;
}) {
  return (
    <div className="rounded-xl border border-ink-200/70 bg-white/50 p-3 dark:border-night-500/30 dark:bg-night-800/60">
      <p className="text-xs font-semibold uppercase text-ink-500 dark:text-night-200">{label}</p>
      {value === null ? (
        <Skeleton className="mt-2 h-6 w-24" />
      ) : (
        <p className="mt-1 text-base font-bold text-ink-950 num-tight dark:text-night-50">
          {value}
        </p>
      )}
    </div>
  );
}

function ActivityChart({
  items,
  loading,
}: {
  items: Array<{ label: string; value: number; max: number; display: string }>;
  loading: boolean;
}) {
  return (
    <div className="mt-4 h-44 rounded-xl border border-ink-200/70 bg-white/40 p-4 dark:border-night-500/30 dark:bg-night-800/60">
      {loading ? (
        <Skeleton className="h-full w-full" />
      ) : (
        <div className="grid h-full grid-cols-6 items-end gap-2">
          {items.map((item, index) => {
            const height = Math.max(8, Math.round((item.value / item.max) * 100));
            return (
              <div key={item.label} className="flex h-full min-w-0 flex-col items-center justify-end gap-2">
                <span className="text-xs font-bold text-ink-700 num-tight dark:text-night-100">
                  {item.display}
                </span>
                <span className="flex h-[72%] w-full items-end rounded-t-lg bg-ink-100 dark:bg-night-700">
                  <span
                    className="student-portal-bar block w-full origin-bottom rounded-t-lg bg-accent/75 dark:bg-beam/75"
                    style={{
                      height: height + '%',
                      animation: 'studentPortalBar 620ms cubic-bezier(0.22, 1, 0.36, 1) both',
                      animationDelay: 120 + index * 55 + 'ms',
                    }}
                  />
                </span>
                <span className="truncate text-[10px] font-semibold text-ink-500 dark:text-night-200">
                  {item.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DotMatrix({
  count,
  active,
  small = false,
}: {
  count: number;
  active: number;
  small?: boolean;
}) {
  return (
    <div className={cn('mt-5 grid grid-cols-12 gap-1', small && 'grid-cols-12')}>
      {Array.from({ length: count }, (_, index) => (
        <span
          key={index}
          className={cn(
            'rounded-full',
            small ? 'h-1.5 w-1.5' : 'h-2 w-2',
            index < active ? 'bg-accent dark:bg-beam' : 'bg-ink-200 dark:bg-night-500/40',
          )}
        />
      ))}
    </div>
  );
}

function RadialMeter({ value }: { value: number }) {
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - value / 100);
  return (
    <svg width="104" height="104" viewBox="0 0 104 104" aria-hidden="true" className="shrink-0">
      <circle
        cx="52"
        cy="52"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="10"
        className="text-ink-200 dark:text-night-500/40"
      />
      <circle
        cx="52"
        cy="52"
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth="10"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        strokeLinecap="round"
        transform="rotate(-90 52 52)"
        className="text-accent transition-[stroke-dashoffset] duration-500 dark:text-beam"
      />
      <text x="52" y="58" textAnchor="middle" className="fill-current text-lg font-bold text-ink-950 dark:text-night-50">
        {value}%
      </text>
    </svg>
  );
}

function ayahKey(surahId: number, ayahNumber: number): string {
  return surahId + ':' + ayahNumber;
}

function numberText(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return minutes + 'm';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? hours + 'h ' + mins + 'm' : hours + 'h';
}
